<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { NButton, NEmpty, NInput, NModal, NTabs, NTabPane, useMessage } from 'naive-ui'
import { blurModalSearchFocus } from '@/utils/blurModalSearchFocus'
import VariableOperationEditor from '@/components/eventEditors/VariableOperationEditor.vue'
import { useWorkspaceStore } from '@/store/workspace'
import { useSceneEditorStore } from '@/store/sceneEditor'
import Var from '@/model/Var'
import Str from '@/model/Str'
import {
  VARIABLE_BATCH_GROUP_SIZE,
  buildVariableBatchEvents,
  cloneNumericOperationForm,
  cloneStringOperationForm,
  createDefaultNumericOperationForm,
  createDefaultStringOperationForm,
  type NumericOperationForm,
  type StringOperationForm,
  type VariableBatchTabKey,
} from '@/features/event/variableBatch'

type NumericConfigMap = Record<number, NumericOperationForm>
type StringConfigMap = Record<number, StringOperationForm>
type VariableEntry = Var | Str

type NumericTabState = {
  groupStart: number
  focusedIndex: number | null
  selectedIndices: number[]
  searchText: string
  form: NumericOperationForm
  configs: NumericConfigMap
}

type StringTabState = {
  groupStart: number
  focusedIndex: number | null
  selectedIndices: number[]
  searchText: string
  form: StringOperationForm
  configs: StringConfigMap
}

const props = defineProps<{
  show: boolean
}>()

const emit = defineEmits<{
  'update:show': [value: boolean]
  inserted: [count: number]
}>()

const message = useMessage()
const workspace = useWorkspaceStore()
const editor = useSceneEditorStore()

const activeTab = ref<VariableBatchTabKey>('vars')
const varsState = ref<NumericTabState>(createNumericTabState())
const varExState = ref<NumericTabState>(createNumericTabState())
const stringsState = ref<StringTabState>(createStringTabState())
const variableDataRevision = ref(0)
const maxEditOpen = ref(false)
const maxEditValue = ref('100')
const lastEntryClick = ref<{ tab: VariableBatchTabKey; index: number; time: number } | null>(null)
const focusedNameValue = ref('')

const project = computed(() => workspace.project)
const varNames = computed(() => getEntries('vars').map((item) => item?.Name ?? ''))
const varExNames = computed(() => getEntries('varEx').map((item) => item?.Name ?? ''))
const stringNames = computed(() => getEntries('strings').map((item) => item?.Name ?? ''))

function createNumericTabState(): NumericTabState {
  return {
    groupStart: 0,
    focusedIndex: null,
    selectedIndices: [],
    searchText: '',
    form: createDefaultNumericOperationForm(),
    configs: {},
  }
}

function createStringTabState(): StringTabState {
  return {
    groupStart: 0,
    focusedIndex: null,
    selectedIndices: [],
    searchText: '',
    form: createDefaultStringOperationForm(),
    configs: {},
  }
}

function resetState() {
  activeTab.value = 'vars'
  varsState.value = createNumericTabState()
  varExState.value = createNumericTabState()
  stringsState.value = createStringTabState()
  lastEntryClick.value = null
  focusedNameValue.value = ''
}

function getStateByTab(tab: VariableBatchTabKey): NumericTabState | StringTabState {
  if (tab === 'vars') return varsState.value
  if (tab === 'varEx') return varExState.value
  return stringsState.value
}

function focusDefaultEntry(tab: VariableBatchTabKey): void {
  const entries = getEntries(tab)
  const state = getStateByTab(tab)
  if (!entries.length) {
    state.focusedIndex = null
    state.selectedIndices = []
    syncFocusedNameValue()
    return
  }
  const start = Math.min(state.groupStart, Math.max(0, entries.length - 1))
  const nextIndex = state.focusedIndex !== null && state.focusedIndex < entries.length
    ? state.focusedIndex
    : start
  if (tab === 'strings') {
    focusStringConfig(stringsState.value, nextIndex)
  } else {
    focusNumericConfig(tab === 'vars' ? varsState.value : varExState.value, nextIndex)
  }
  syncFocusedNameValue()
}

function getEntries(tab: VariableBatchTabKey): VariableEntry[] {
  variableDataRevision.value
  if (tab === 'vars') return project.value?.gameVar?.Data ?? []
  if (tab === 'varEx') return project.value?.gameVarEx?.Data ?? []
  return project.value?.gameStr?.Data ?? []
}

function getCurrentGroupStart() {
  if (activeTab.value === 'vars') return varsState.value.groupStart
  if (activeTab.value === 'varEx') return varExState.value.groupStart
  return stringsState.value.groupStart
}

function getCurrentFocusedIndex() {
  if (activeTab.value === 'vars') return varsState.value.focusedIndex
  if (activeTab.value === 'varEx') return varExState.value.focusedIndex
  return stringsState.value.focusedIndex
}

function getCurrentSelectedIndices() {
  if (activeTab.value === 'vars') return varsState.value.selectedIndices
  if (activeTab.value === 'varEx') return varExState.value.selectedIndices
  return stringsState.value.selectedIndices
}

function getCurrentSearchText() {
  if (activeTab.value === 'vars') return varsState.value.searchText
  if (activeTab.value === 'varEx') return varExState.value.searchText
  return stringsState.value.searchText
}

function setCurrentGroupStart(value: number) {
  if (activeTab.value === 'vars') varsState.value.groupStart = value
  else if (activeTab.value === 'varEx') varExState.value.groupStart = value
  else stringsState.value.groupStart = value
}

function setCurrentSearchText(value: string) {
  if (activeTab.value === 'vars') varsState.value.searchText = value
  else if (activeTab.value === 'varEx') varExState.value.searchText = value
  else stringsState.value.searchText = value
}

const currentEntries = computed(() => getEntries(activeTab.value))
const currentSearchText = computed({
  get: getCurrentSearchText,
  set: setCurrentSearchText,
})

const currentGroupOptions = computed(() => {
  variableDataRevision.value
  const total = currentEntries.value.length
  const groups: Array<{ start: number; end: number; label: string }> = []
  for (let start = 0; start < total; start += VARIABLE_BATCH_GROUP_SIZE) {
    const end = Math.min(start + VARIABLE_BATCH_GROUP_SIZE, total)
    groups.push({
      start,
      end,
      label: `[ ${String(start + 1).padStart(3, '0')} - ${String(end).padStart(3, '0')} ]`,
    })
  }
  return groups
})

const visibleEntries = computed(() => {
  variableDataRevision.value
  const source = currentEntries.value
  const keyword = currentSearchText.value.trim().toLowerCase()
  if (keyword) {
    return source
      .map((item, index) => ({
        index,
        name: item?.Name ?? '',
      }))
      .filter((item) => `${item.index + 1} ${item.name}`.toLowerCase().includes(keyword))
  }

  const start = getCurrentGroupStart()
  const end = Math.min(start + VARIABLE_BATCH_GROUP_SIZE, source.length)
  const list: Array<{ index: number; name: string }> = []
  for (let index = start; index < end; index += 1) {
    list.push({
      index,
      name: source[index]?.Name ?? '',
    })
  }
  return list
})

const currentTitle = computed(() => {
  if (activeTab.value === 'vars') return '数值'
  if (activeTab.value === 'varEx') return '二周目'
  return '字符串'
})
const currentDefaultMax = computed(() => (activeTab.value === 'strings' ? 50 : 100))
const focusedEntry = computed(() => {
  variableDataRevision.value
  const index = getCurrentFocusedIndex()
  return index === null ? null : currentEntries.value[index] ?? null
})

function syncFocusedNameValue() {
  focusedNameValue.value = focusedEntry.value?.Name ?? ''
}

function updateFocusedNameValue(value: string) {
  focusedNameValue.value = value
  const entry = focusedEntry.value
  if (!entry) return
  entry.Name = value
  variableDataRevision.value += 1
}

const currentIndexLabel = computed(() => (activeTab.value === 'strings' ? '字符串编号' : '变量编号'))
const currentIndexValue = computed(() => getCurrentFocusedIndex() ?? '')
const currentIndexDisplayLabel = computed(() => {
  variableDataRevision.value
  const index = getCurrentFocusedIndex()
  if (index === null) return ''
  const entry = currentEntries.value[index]
  const name = String(entry?.Name ?? '').trim() || '未命名'
  return `${String(index + 1).padStart(3, '0')}: ${name}`
})

function normalizeIndices(indices: number[]) {
  return [...new Set(indices)].sort((a, b) => a - b)
}

function ensureNumericConfig(state: NumericTabState, index: number): NumericOperationForm {
  if (!state.configs[index]) {
    state.configs[index] = cloneNumericOperationForm(state.form)
  }
  return state.configs[index]
}

function ensureStringConfig(state: StringTabState, index: number): StringOperationForm {
  if (!state.configs[index]) {
    state.configs[index] = cloneStringOperationForm(state.form)
  }
  return state.configs[index]
}

function focusNumericConfig(state: NumericTabState, index: number) {
  state.focusedIndex = index
  state.form = cloneNumericOperationForm(ensureNumericConfig(state, index))
  state.form.isIndex = false
  state.configs[index] = cloneNumericOperationForm(state.form)
}

function focusStringConfig(state: StringTabState, index: number) {
  state.focusedIndex = index
  state.form = cloneStringOperationForm(ensureStringConfig(state, index))
  state.form.isVarIndex = false
  state.configs[index] = cloneStringOperationForm(state.form)
}

function setFocusedIndex(index: number) {
  if (activeTab.value === 'vars') {
    focusNumericConfig(varsState.value, index)
    syncFocusedNameValue()
    return
  }
  if (activeTab.value === 'varEx') {
    focusNumericConfig(varExState.value, index)
    syncFocusedNameValue()
    return
  }
  focusStringConfig(stringsState.value, index)
  syncFocusedNameValue()
}

function selectGroup(start: number) {
  setCurrentGroupStart(start)
  if (!currentSearchText.value.trim()) {
    setFocusedIndex(start)
  }
}

function toggleEntry(index: number) {
  if (activeTab.value === 'vars') {
    const current = varsState.value.selectedIndices
    const hasSelected = current.includes(index)
    if (!hasSelected) ensureNumericConfig(varsState.value, index)
    varsState.value.selectedIndices = hasSelected
      ? current.filter((item) => item !== index)
      : normalizeIndices([...current, index])
  } else if (activeTab.value === 'varEx') {
    const current = varExState.value.selectedIndices
    const hasSelected = current.includes(index)
    if (!hasSelected) ensureNumericConfig(varExState.value, index)
    varExState.value.selectedIndices = hasSelected
      ? current.filter((item) => item !== index)
      : normalizeIndices([...current, index])
  } else {
    const current = stringsState.value.selectedIndices
    const hasSelected = current.includes(index)
    if (!hasSelected) ensureStringConfig(stringsState.value, index)
    stringsState.value.selectedIndices = hasSelected
      ? current.filter((item) => item !== index)
      : normalizeIndices([...current, index])
  }
}

function updateNumericForm(tab: 'vars' | 'varEx', value: NumericOperationForm | StringOperationForm) {
  const state = tab === 'vars' ? varsState.value : varExState.value
  const next = cloneNumericOperationForm(value as NumericOperationForm)
  next.isIndex = false
  state.form = next
  if (state.focusedIndex !== null) {
    state.configs[state.focusedIndex] = cloneNumericOperationForm(next)
  }
}

function updateStringForm(value: NumericOperationForm | StringOperationForm) {
  const next = cloneStringOperationForm(value as StringOperationForm)
  next.isVarIndex = false
  stringsState.value.form = next
  if (stringsState.value.focusedIndex !== null) {
    stringsState.value.configs[stringsState.value.focusedIndex] = cloneStringOperationForm(next)
  }
}

function focusEntry(index: number) {
  setFocusedIndex(index)
}

function handleEntryClick(index: number) {
  const now = Date.now()
  const last = lastEntryClick.value
  focusEntry(index)
  if (last && last.tab === activeTab.value && last.index === index && now - last.time < 360) {
    toggleEntry(index)
    lastEntryClick.value = null
    return
  }
  lastEntryClick.value = { tab: activeTab.value, index, time: now }
}

function isSelected(index: number) {
  return getCurrentSelectedIndices().includes(index)
}

function close() {
  emit('update:show', false)
}

function handleConfirm() {
  const vars = resolveNumericSelectedConfigs(varsState.value)
  const varEx = resolveNumericSelectedConfigs(varExState.value)
  const strings = resolveStringSelectedConfigs(stringsState.value)
  const events = buildVariableBatchEvents({
    vars,
    varEx,
    strings,
    varNames: varNames.value,
    varExNames: varExNames.value,
  })
  if (!events.length) {
    emit('update:show', false)
    return
  }
  const inserted = editor.insertEventBlock(events)
  if (!inserted?.length) {
    message.error('变量操作批量插入失败')
    return
  }
  emit('inserted', inserted.length)
  emit('update:show', false)
}

function resolveNumericSelectedConfigs(state: NumericTabState): NumericConfigMap {
  const configs: NumericConfigMap = {}
  for (const index of state.selectedIndices) {
    configs[index] = cloneNumericOperationForm(state.configs[index] ?? state.form)
  }
  return configs
}

function resolveStringSelectedConfigs(state: StringTabState): StringConfigMap {
  const configs: StringConfigMap = {}
  for (const index of state.selectedIndices) {
    configs[index] = cloneStringOperationForm(state.configs[index] ?? state.form)
  }
  return configs
}

function createEntry(tab: VariableBatchTabKey): VariableEntry {
  return tab === 'strings' ? new Str('', '') : new Var(0, '')
}

function openMaxEditor() {
  maxEditValue.value = String(currentEntries.value.length || currentDefaultMax.value)
  maxEditOpen.value = true
}

function applyMaxCount() {
  const next = Number(maxEditValue.value)
  if (!Number.isInteger(next) || next <= 0) {
    message.warning('最大值必须是正整数')
    return
  }
  const entries = currentEntries.value
  if (next > entries.length) {
    for (let i = entries.length; i < next; i += 1) {
      entries.push(createEntry(activeTab.value))
    }
  } else if (next < entries.length) {
    entries.splice(next)
    trimStateByMax(getCurrentState(), next)
  }
  maxEditOpen.value = false
  variableDataRevision.value += 1
  focusDefaultEntry(activeTab.value)
  syncFocusedNameValue()
}

function getCurrentState() {
  if (activeTab.value === 'vars') return varsState.value
  if (activeTab.value === 'varEx') return varExState.value
  return stringsState.value
}

function trimStateByMax(state: NumericTabState | StringTabState, max: number) {
  state.selectedIndices = state.selectedIndices.filter((index) => index < max)
  for (const key of Object.keys(state.configs)) {
    if (Number(key) >= max) delete state.configs[Number(key)]
  }
  if (state.focusedIndex !== null && state.focusedIndex >= max) {
    state.focusedIndex = max ? max - 1 : null
  }
}

watch(
  () => props.show,
  (show) => {
    if (show) {
      resetState()
      focusDefaultEntry(activeTab.value)
      return
    }
    resetState()
  },
  { immediate: true },
)

watch(
  () => activeTab.value,
  (tab) => {
    if (!props.show) return
    focusDefaultEntry(tab)
  },
)
</script>

<template>
  <n-modal
    :show="show"
    preset="card"
    title="变量操作"
    class="variable-batch-modal"
    :mask-closable="true"
    :auto-focus="false"
    :trap-focus="false"
    @after-enter="blurModalSearchFocus('.variable-batch-modal')"
    @update:show="emit('update:show', $event)"
  >
    <div v-if="project" class="variable-batch">
      <n-tabs v-model:value="activeTab" type="line" :animated="false" display-directive="show">
        <n-tab-pane name="vars" tab="数值" />
        <n-tab-pane name="varEx" tab="二周目" />
        <n-tab-pane name="strings" tab="字符串" />
      </n-tabs>

      <div class="batch-toolbar">
        <span class="batch-caption">{{ currentTitle }}，共 {{ currentEntries.length }} 个</span>
        <n-input
          v-model:value="currentSearchText"
          size="small"
          clearable
          :autofocus="false"
          placeholder="搜索编号或名称"
          class="batch-search"
        />
        <n-button size="small" secondary @click="openMaxEditor">修改最大值</n-button>
      </div>

      <div class="batch-layout">
        <section class="batch-panel">
          <div class="batch-panel__title">索引范围</div>
          <div v-if="currentGroupOptions.length" class="batch-panel__list">
            <button
              v-for="group in currentGroupOptions"
              :key="group.start"
              type="button"
              class="batch-list-btn"
              :class="{ 'batch-list-btn--active': getCurrentGroupStart() === group.start && !currentSearchText.trim() }"
              @click="selectGroup(group.start)"
            >
              {{ group.label }}
            </button>
          </div>
          <n-empty v-else description="暂无变量" size="small" />
        </section>

        <section class="batch-panel">
          <div class="batch-panel__title">{{ currentSearchText.trim() ? '搜索结果' : '具体索引' }}</div>
          <div v-if="visibleEntries.length" class="batch-panel__list">
            <div
              v-for="item in visibleEntries"
              :key="item.index"
              class="batch-entry"
              :class="{
                'batch-entry--focused': getCurrentFocusedIndex() === item.index,
                'batch-entry--selected': isSelected(item.index),
              }"
              @click="handleEntryClick(item.index)"
            >
              <input
                class="batch-entry__check"
                type="checkbox"
                :checked="isSelected(item.index)"
                @click.stop="focusEntry(item.index)"
                @change="toggleEntry(item.index)"
              />
              <div
                class="batch-entry__button"
                @click.stop="handleEntryClick(item.index)"
              >
                <span class="batch-entry__index">{{ String(item.index + 1).padStart(3, '0') }}</span>
                <span class="batch-entry__name">{{ item.name || '未命名' }}</span>
              </div>
            </div>
          </div>
          <n-empty v-else description="当前没有可显示的变量" size="small" />
        </section>

        <section class="batch-panel batch-panel--editor">
          <div class="batch-panel__title">操作属性</div>
          <div class="batch-form">
            <label class="batch-name-row">
              <span class="batch-name-row__label">名称</span>
              <input
                class="batch-name-row__input"
                :value="focusedNameValue"
                :disabled="!focusedEntry"
                placeholder="未命名"
                @input="updateFocusedNameValue(($event.target as HTMLInputElement).value)"
              />
            </label>

            <template v-if="activeTab === 'strings'">
              <VariableOperationEditor
                mode="string"
                :model-value="stringsState.form"
                :index-label="currentIndexLabel"
                :index-value="currentIndexValue"
                :index-display-label="currentIndexDisplayLabel"
                index-picker-scope="string"
                :index-readonly="true"
                :variable-label-revision="variableDataRevision"
                @update:model-value="updateStringForm"
              />
            </template>

            <template v-else-if="activeTab === 'vars'">
              <VariableOperationEditor
                mode="numeric"
                :model-value="varsState.form"
                :index-label="currentIndexLabel"
                :index-value="currentIndexValue"
                :index-display-label="currentIndexDisplayLabel"
                index-picker-scope="var"
                :index-readonly="true"
                :variable-label-revision="variableDataRevision"
                @update:model-value="updateNumericForm('vars', $event)"
              />
            </template>

            <template v-else>
              <VariableOperationEditor
                mode="numeric"
                :model-value="varExState.form"
                :index-label="currentIndexLabel"
                :index-value="currentIndexValue"
                :index-display-label="currentIndexDisplayLabel"
                index-picker-scope="varEx"
                :index-readonly="true"
                :variable-label-revision="variableDataRevision"
                @update:model-value="updateNumericForm('varEx', $event)"
              />
            </template>
          </div>
        </section>
      </div>

      <div class="batch-footer">
        <n-button size="small" type="primary" @click="handleConfirm">确定</n-button>
        <n-button size="small" @click="close">取消</n-button>
      </div>
    </div>

    <n-empty v-else description="当前没有打开的工程" size="small" />

    <n-modal
      :show="maxEditOpen"
      preset="card"
      title="修改最大值"
      class="variable-max-modal"
      :mask-closable="false"
      :auto-focus="false"
      @after-enter="blurModalSearchFocus('.variable-max-modal')"
      @update:show="maxEditOpen = $event"
    >
      <div class="max-edit-body">
        <div class="max-edit-hint">请输入 {{ currentTitle }}变量的最大数量</div>
        <n-input
          v-model:value="maxEditValue"
          size="small"
          :autofocus="false"
          placeholder="请输入正整数"
        />
      </div>
      <template #footer>
        <div class="max-edit-actions">
          <n-button size="small" @click="maxEditOpen = false">取消</n-button>
          <n-button size="small" type="primary" @click="applyMaxCount">确认</n-button>
        </div>
      </template>
    </n-modal>
  </n-modal>
</template>

<style scoped>
.variable-batch {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
}

.batch-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--theme-border);
}

.batch-caption {
  min-width: 0;
  color: var(--theme-text-secondary);
  font-size: 11px;
}

.batch-search {
  width: 220px;
}

.batch-layout {
  display: grid;
  grid-template-columns: 180px 240px minmax(0, 1fr);
  gap: 8px;
  min-height: 0;
  height: min(54vh, 520px);
}

.batch-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
}

.batch-panel--editor {
  min-width: 0;
}

.batch-panel__title {
  display: flex;
  align-items: center;
  padding: 7px 10px;
  border-bottom: 1px solid var(--theme-border);
  background: var(--theme-bg-raised);
  color: var(--theme-text-secondary);
  font-size: 11px;
}

.batch-panel__list {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.batch-list-btn {
  padding: 6px 8px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
  color: var(--theme-text-heading);
  text-align: left;
  cursor: pointer;
}

.batch-list-btn--active {
  border-color: var(--theme-accent);
  background: var(--theme-bg-selected);
}

.batch-entry {
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr) auto;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
}

.batch-entry--focused {
  border-color: var(--theme-accent);
  background: var(--theme-bg-selected);
}

.batch-entry--selected {
  background: color-mix(in srgb, var(--theme-accent) 12%, var(--theme-bg-panel));
}

.batch-entry__check {
  margin-left: 6px;
}

.batch-entry__button {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr);
  gap: 6px;
  width: 100%;
  padding: 6px 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.batch-entry__index {
  color: var(--theme-text-secondary);
  font-size: 11px;
}

.batch-entry__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--theme-text-heading);
  font-size: 12px;
}

.batch-form {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.batch-name-row {
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--theme-border);
}

.batch-name-row__label {
  color: var(--theme-text-secondary);
  font-size: 12px;
}

.batch-name-row__input {
  min-width: 0;
  padding: 5px 7px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
  color: var(--theme-text-heading);
  font-size: 12px;
}

.batch-footer {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  padding-top: 6px;
  border-top: 1px solid var(--theme-border);
}

.batch-form :deep(.variable-operation-editor) {
  gap: 0;
}

.batch-form :deep(.variable-row),
.batch-form :deep(.prop-row) {
  padding-left: 0;
  padding-right: 0;
}

.batch-form :deep(.variable-input),
.batch-form :deep(.cell-input) {
  border-color: var(--theme-border);
  background: var(--theme-bg-panel);
}

:global(body .variable-batch-modal.n-modal) {
  width: min(1080px, 96vw);
  max-width: 96vw;
}

:global(body .variable-batch-modal .n-card-header) {
  border-bottom: 1px solid var(--theme-border);
}

:global(body .variable-max-modal.n-modal) {
  width: min(360px, 92vw);
}

.max-edit-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.max-edit-hint {
  color: var(--theme-text-secondary);
  font-size: 12px;
}

.max-edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

@media (max-width: 900px) and (orientation: portrait) {
  .batch-layout {
    grid-template-columns: 1fr;
    height: auto;
  }

  .batch-panel {
    max-height: 220px;
  }
}
</style>
